// Auth Error Handling - Modular
export class AuthError extends Error {
  constructor(message: string, public code?: string) {
    super(message);
    this.name = 'AuthError';
  }
}

export const AUTH_ERROR_CODES = {
  EMAIL_NOT_CONFIRMED: 'EMAIL_NOT_CONFIRMED',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  ACCOUNT_SUSPENDED: 'ACCOUNT_SUSPENDED',
  ACCOUNT_DELETED: 'ACCOUNT_DELETED',
  NETWORK_ERROR: 'NETWORK_ERROR',
  RATE_LIMITED: 'RATE_LIMITED',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;

export function handleAuthError(error: any): AuthError {
  const message = error?.message || 'An authentication error occurred';
  
  if (message.includes('Email not confirmed') || message.includes('email_not_confirmed') || message.includes('Email not verified')) {
    return new AuthError(message, AUTH_ERROR_CODES.EMAIL_NOT_CONFIRMED);
  }
  
  if (message.includes('Invalid login credentials') || message.includes('invalid_credentials')) {
    return new AuthError('Invalid email or password', AUTH_ERROR_CODES.INVALID_CREDENTIALS);
  }
  
  if (message.includes('Password') && (message.includes('length') || message.includes('short') || message.includes('minimum'))) {
    return new AuthError('Password must be at least 8 characters long.', AUTH_ERROR_CODES.UNKNOWN_ERROR);
  }
  
  if (message.includes('ACCOUNT_SUSPENDED') || message.includes('suspended')) {
    return new AuthError('Your account has been suspended. Please contact info@fyle-cloud.com for more information.', AUTH_ERROR_CODES.ACCOUNT_SUSPENDED);
  }
  
  if (message.includes('ACCOUNT_DELETED') || message.includes('deleted') || message.includes('account was deleted')) {
    return new AuthError('This account has been deleted. If you believe this is an error, please contact info@fyle-cloud.com.', AUTH_ERROR_CODES.ACCOUNT_DELETED);
  }
  
  if (message.includes('rate limit') || message.includes('too many')) {
    return new AuthError('Too many requests. Please try again later', AUTH_ERROR_CODES.RATE_LIMITED);
  }
  
  if (message.includes('network') || message.includes('fetch')) {
    return new AuthError('Network error. Please check your connection', AUTH_ERROR_CODES.NETWORK_ERROR);
  }
  
  return new AuthError(message, AUTH_ERROR_CODES.UNKNOWN_ERROR);
}

export function getAuthErrorMessage(error: AuthError | string): string {
  if (typeof error === 'string') return error;
  
  switch (error.code) {
    case AUTH_ERROR_CODES.EMAIL_NOT_CONFIRMED:
      return '⚠️ Your email address has not been confirmed yet! Please check your inbox and click on the confirmation link in the email we sent you. Also check your spam folder.';
    case AUTH_ERROR_CODES.INVALID_CREDENTIALS:
      return 'Invalid login credentials. Please check your email address and password.';
    case AUTH_ERROR_CODES.ACCOUNT_SUSPENDED:
      return 'Your account has been suspended. Please contact info@fyle-cloud.com for more information.';
    case AUTH_ERROR_CODES.ACCOUNT_DELETED:
      return 'This account was deleted within the last 30 days. If you believe this is an error, please contact info@fyle-cloud.com.';
    case AUTH_ERROR_CODES.RATE_LIMITED:
      return 'Too many requests. Please wait a moment.';
    case AUTH_ERROR_CODES.NETWORK_ERROR:
      return 'Network error. Please check your internet connection.';
    default:
      return error.message || 'An error occurred. Please try again.';
  }
}

